import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Div, Dropdown, Flex, Link, Span } from '@gaiads/telia-react-component-library';

import { RootState } from '../../store';
import { selectEditingLocaleId, selectReferenceLocaleId, setEditingLocaleId, setReferenceLocaleId } from '../../store/editingLocale';
import { selectAllLocales, selectCurrentLocaleId, selectLocaleById } from '../../store/locales';

import './EditingLocale.scss';
import { HTMLElementProps } from '../../utils/types';

interface EditingLocaleProps {
  hideEditingLocale?: boolean;
  accordionProps?: Partial<React.ComponentProps<typeof Accordion> & HTMLElementProps>;
}
export default function EditingLocale({ hideEditingLocale, accordionProps }: EditingLocaleProps) {
  const locales = useSelector(selectAllLocales);

  const editingLocaleId = useSelector(selectEditingLocaleId);
  const referenceLocaleId = useSelector(selectReferenceLocaleId);

  const editingLocale = useSelector((state: RootState) => (editingLocaleId ? selectLocaleById(state, editingLocaleId) : undefined));
  const referenceLocale = useSelector((state: RootState) => (referenceLocaleId ? selectLocaleById(state, referenceLocaleId) : undefined));

  const localeId = useSelector(selectCurrentLocaleId);

  const dispatch = useDispatch();

  const { t } = useTranslation('editingLocale');

  const fallbackOption = {
    value: '0',
    label: t('fallbackOption'),
  };

  const reset = () => {
    dispatch(setEditingLocaleId(localeId));
    dispatch(setReferenceLocaleId(0));
  };

  const handleEditingLocaleChange = (id?: string) => {
    dispatch(setEditingLocaleId(Number(id)));
  };
  const handleReferenceLocaleChange = (id?: string) => {
    dispatch(setReferenceLocaleId(Number(id)));
  };

  useEffect(() => {
    dispatch(setEditingLocaleId(localeId));
    dispatch(setReferenceLocaleId(0));
    // Set editing locale to current locale on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Div className={'editing-locale__container'}>
      <Accordion heading={t('heading')} showOverflow={true} initiallyExpanded={true} {...accordionProps}>
        {!hideEditingLocale && (
          <Div margin={{ bottom: 'md' }}>
            <Dropdown
              label={t('labels.editingLocaleDropdown')}
              selectedOption={
                editingLocale
                  ? {
                      value: String(editingLocale.id),
                      label: editingLocale.name,
                    }
                  : undefined
              }
              options={[
                ...locales.map(locale => ({
                  value: String(locale.id),
                  label: locale.name,
                })),
              ]}
              onChange={(event, option) => handleEditingLocaleChange(option.value)}
            />
          </Div>
        )}
        <Div margin={{ vertical: 'md' }}>
          <Dropdown
            label={t('labels.referenceLocaleDropdown')}
            selectedOption={
              referenceLocale
                ? {
                    value: String(referenceLocale.id),
                    label: referenceLocale.name,
                  }
                : fallbackOption
            }
            options={[
              fallbackOption,
              ...locales.map(locale => ({
                value: String(locale.id),
                label: locale.name,
              })),
            ]}
            onChange={(event, option) => handleReferenceLocaleChange(option.value)}
          />
        </Div>
        <Flex margin={{ top: 'md' }} spaceBetween centeredVertically>
          <Span onClick={reset}>
            <Link>{t('links.reset')}</Link>
          </Span>
        </Flex>
      </Accordion>
    </Div>
  );
}
